import { escapeReceiptHtml, formatReceiptMoney } from './format';
import {
  RECEIPT_FONT_FAMILY,
  RECEIPT_INK,
  receiptPlainDocumentCss,
  type ThermalPlainLineRole,
} from './receiptTypography';
import type { ReceiptWidthMm } from './types';

export type { ThermalPlainLineRole } from './receiptTypography';
export { receiptPlainTextCols } from './format';

const DEFAULT_WIDTH: ReceiptWidthMm = 72;

function pageMarginMm(widthMm: ReceiptWidthMm): number {
  if (widthMm === 58) return 2;
  if (widthMm === 80) return 4;
  return 3;
}

function bodyPaddingPx(widthMm: ReceiptWidthMm): string {
  if (widthMm === 58) return '4px 2px';
  if (widthMm === 80) return '6px 8px';
  return '6px 4px';
}

function titleTypography(widthMm: ReceiptWidthMm): { fontSize: number; letterSpacing: string } {
  if (widthMm === 58) return { fontSize: 14, letterSpacing: 'normal' };
  if (widthMm === 80) return { fontSize: 16, letterSpacing: 'normal' };
  return { fontSize: 16, letterSpacing: 'normal' };
}

export function thermalHtmlStyles(widthMm: ReceiptWidthMm = DEFAULT_WIDTH): string {
  const margin = pageMarginMm(widthMm);
  const title = titleTypography(widthMm);
  return `
  @page { margin: ${margin}mm; size: ${widthMm}mm auto; }
  @media print {
    html, body {
      margin: 0 !important;
      width: auto !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }
  }
  @media screen {
    body {
      max-width: ${widthMm}mm;
      width: ${widthMm}mm;
    }
  }
  body {
    font-family: ${RECEIPT_FONT_FAMILY};
    font-size: 12px; color: ${RECEIPT_INK}; margin: 0; padding: ${bodyPaddingPx(widthMm)};
    box-sizing: border-box;
  }
  .title {
    text-align: center; font-size: ${title.fontSize}px; font-weight: 800;
    letter-spacing: ${title.letterSpacing}; line-height: 1.15; margin: 4px 0 2px;
    word-wrap: break-word; overflow-wrap: anywhere;
  }
  .center { text-align: center; }
  .meta { margin: 2px 0; }
  .sep { border: none; border-top: 1px solid #111; margin: 8px 0; }
  table { width: 100%; border-collapse: collapse; }
  td { vertical-align: top; padding: 2px 0; }
  td.qty { width: 2.2em; white-space: nowrap; }
  td.name { word-break: break-word; }
  td.name .mod { font-size: 11px; margin-top: 1px; }
  td.price { text-align: right; white-space: nowrap; padding-left: 6px; }
  .row { margin: 2px 0; }
  .total { font-weight: 800; font-size: 14px; margin-top: 4px; }
  .foot { text-align: center; font-size: 10px; margin-top: 10px; }
  .void-banner {
    text-align: center; font-weight: 800; font-size: 14px;
    border: 2px solid #111; padding: 4px; margin: 6px 0;
  }
`;
}

export function wrapThermalHtml(body: string, widthMm: ReceiptWidthMm = DEFAULT_WIDTH): string {
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"/>
<style>${thermalHtmlStyles(widthMm)}</style></head><body>
${body}
</body></html>`;
}

/**
 * Ticket monospace en `<pre>` — alinea con ESC/POS y evita que el browser
 * recorte títulos/tablas por CSS en mm (térmicas Windows).
 */
export function wrapThermalPlainTextHtml(
  plainText: string,
  widthMm: ReceiptWidthMm = DEFAULT_WIDTH,
): string {
  return wrapThermalPlainDocumentHtml({
    widthMm,
    headerLines: plainText.split('\n').map((text) => ({ text, role: 'meta' as const })),
    itemsPre: '',
    footerLines: [],
  });
}

/** Encabezado con jerarquía + cuerpo monospace (térmicas Windows). */
export function wrapThermalPlainDocumentHtml(args: {
  widthMm?: ReceiptWidthMm;
  headerLines: Array<{ text: string; role: ThermalPlainLineRole }>;
  itemsPre: string;
  footerLines: Array<{ text: string; role: ThermalPlainLineRole }>;
  appendHtml?: string;
}): string {
  const widthMm = args.widthMm ?? DEFAULT_WIDTH;
  const headerHtml = args.headerLines
    .map(
      (line) =>
        `<div class="line ${line.role}">${escapeReceiptHtml(line.text)}</div>`,
    )
    .join('');
  const itemsSafe = escapeReceiptHtml(args.itemsPre.trimEnd());
  const itemsBlock = itemsSafe
    ? `<pre class="items">${itemsSafe}</pre>`
    : '';
  const footerHtml = args.footerLines
    .map(
      (line) =>
        `<div class="line ${line.role}">${escapeReceiptHtml(line.text)}</div>`,
    )
    .join('');
  const append = args.appendHtml?.trim()
    ? `<div class="append">${args.appendHtml}</div>`
    : '';
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"/>
<style>${receiptPlainDocumentCss(widthMm)}</style></head><body>
${headerHtml}${itemsBlock}${footerHtml}${append}
</body></html>`;
}

export function thermalItemRowsHtml(
  lines: Array<{
    name: string;
    qty: number;
    lineTotal: number;
    optionsLabel?: string | null;
    lineNote?: string | null;
  }>
): string {
  return lines
    .map((line) => {
      const opts = line.optionsLabel?.trim() || '';
      const note = line.lineNote?.trim() || '';
      const meta = [
        opts ? `<div class="mod">${escapeReceiptHtml(opts)}</div>` : '',
        note ? `<div class="mod">Nota: ${escapeReceiptHtml(note)}</div>` : '',
      ].join('');
      return `<tr><td class="qty">${escapeReceiptHtml(String(line.qty))}×</td><td class="name">${escapeReceiptHtml(line.name)}${meta}</td><td class="price">${formatReceiptMoney(line.lineTotal)}</td></tr>`;
    })
    .join('');
}
